import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tab,
  TableCell,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SchoolIcon from '@mui/icons-material/School';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../config/api';
import DataTable from '../components/DataTable';

const emptyCourse = {
  title: '',
  description: '',
  thumbnail_url: '',
  status: 'published',
  course_type: 'Certification',
  price: '',
  duration: '',
  seats_left: '',
  emi: '',
};

function Courses() {
  const [tab, setTab] = useState(0);

  // Courses state
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyCourse);

  // Enquiries state
  const [enquiries, setEnquiries] = useState([]);
  const [loadingEnquiries, setLoadingEnquiries] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/courses/admin');
      setCourses(response.data?.data || []);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEnquiries = useCallback(async () => {
    try {
      setLoadingEnquiries(true);
      const response = await api.get('/courses/admin/enquiries/list');
      setEnquiries(response.data?.data || []);
    } catch (requestError) {
      // ignore silently if table not yet migrated
    } finally {
      setLoadingEnquiries(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
    fetchEnquiries();
  }, [fetchCourses, fetchEnquiries]);

  const handleOpen = (course = null) => {
    setEditing(course);
    setFormData(
      course
        ? {
            title: course.title || '',
            description: course.description || '',
            thumbnail_url: course.thumbnail_url || '',
            status: course.status || 'published',
            course_type: course.course_type || 'Certification',
            price: course.price || '',
            duration: course.duration || '',
            seats_left: course.seats_left || '',
            emi: course.emi || '',
          }
        : emptyCourse
    );
    setError('');
    setSuccess('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setFormData(emptyCourse);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      const payload = {
        title: (formData.title || '').trim(),
        description: (formData.description || '').trim(),
        thumbnail_url: (formData.thumbnail_url || '').trim(),
        status: formData.status || 'published',
        course_type: formData.course_type || 'Certification',
        price: (formData.price || '').trim(),
        duration: (formData.duration || '').trim(),
        seats_left: (formData.seats_left || '').trim(),
        emi: (formData.emi || '').trim(),
      };
      if (editing) {
        await api.put(`/courses/${editing.id}`, payload);
      } else {
        await api.post('/courses', payload);
      }
      handleClose();
      setSuccess(editing ? 'Course updated' : 'Course created');
      await fetchCourses();
    } catch (requestError) {
      const apiError = requestError.response?.data?.error;
      setError(
        apiError ||
          (requestError.response?.status === 404
            ? 'Course API not found. Deploy the backend with course routes and try again.'
            : 'Failed to save course')
      );
    }
  };

  const handlePublishToggle = async (course) => {
    const status = course.status === 'published' ? 'draft' : 'published';
    try {
      setError('');
      setSuccess('');
      await api.put(`/courses/${course.id}`, { status });
      setSuccess(status === 'published' ? 'Course published' : 'Course moved to drafts');
      await fetchCourses();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to change course status');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        setError('');
        setSuccess('');
        await api.delete(`/courses/${id}`);
        setSuccess('Course deleted successfully');
        await fetchCourses();
      } catch (requestError) {
        setError(requestError.response?.data?.error || 'Failed to delete course');
      }
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      setError('');
      await api.put(`/courses/admin/enquiries/${id}/status`, { status });
      setSuccess('Status updated');
      await fetchEnquiries();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to update enquiry status');
    }
  };

  const handleDeleteEnquiry = async (id) => {
    if (window.confirm('Are you sure you want to delete this enquiry?')) {
      try {
        setError('');
        await api.delete(`/courses/admin/enquiries/${id}`);
        setSuccess('Enquiry deleted successfully');
        await fetchEnquiries();
      } catch (requestError) {
        setError(requestError.response?.data?.error || 'Failed to delete enquiry');
      }
    }
  };

  const openWhatsAppChat = (phone, candidateName, courseTitle) => {
    if (!phone) return;
    const cleanPhone = phone.replace(/\D/g, '');
    const fullNumber = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone;
    const msg = encodeURIComponent(
      `Hello ${candidateName || 'there'}, thank you for inquiring about ${courseTitle || 'our course'} on Mr. Coach Pro. How can we help you?`
    );
    window.open(`https://wa.me/${fullNumber}?text=${msg}`, '_blank');
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Courses & Enquiries</Typography>
        {tab === 0 && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
            Add Course
          </Button>
        )}
      </Box>

      {error && !open && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': {
              minWidth: { xs: 160, sm: 240 },
              py: 1.5,
              px: 3,
              fontSize: '0.95rem',
              fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          <Tab
            icon={<SchoolIcon />}
            iconPosition="start"
            label={`Courses Catalog (${courses.length})`}
          />
          <Tab
            icon={<ContactMailIcon />}
            iconPosition="start"
            label={
              <Badge
                badgeContent={enquiries.filter((e) => e.status === 'new').length}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    right: -16,
                    top: 2,
                    fontSize: '0.75rem',
                    height: 20,
                    minWidth: 20,
                    px: 0.5,
                  },
                }}
              >
                {`Course Enquiries (${enquiries.length})`}
              </Badge>
            }
          />
        </Tabs>
      </Box>

      {tab === 0 && (
        <DataTable
          data={courses}
          loading={loading}
          searchFields={['title', 'description', 'status', 'course_type']}
          emptyMessage="No courses found"
          columns={[
            { field: 'id', label: 'ID' },
            { field: 'title', label: 'Title' },
            { field: 'course_type', label: 'Type' },
            { field: 'price', label: 'Price' },
            { field: 'duration', label: 'Duration' },
            { field: 'status', label: 'Status' },
            { field: 'actions', label: 'Actions' },
          ]}
          renderRow={(course) => (
            <TableRow key={course.id}>
              <TableCell>{course.id}</TableCell>
              <TableCell>{course.title}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={course.course_type || 'Certification'}
                  color={
                    (course.course_type || '').toLowerCase().includes('workshop')
                      ? 'success'
                      : (course.course_type || '').toLowerCase().includes('free')
                        ? 'info'
                        : 'warning'
                  }
                />
              </TableCell>
              <TableCell>{course.price || '-'}</TableCell>
              <TableCell>{course.duration || '-'}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={course.status}
                  color={course.status === 'published' ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell>
                <IconButton
                  aria-label={`Edit ${course.title}`}
                  size="small"
                  onClick={() => handleOpen(course)}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  aria-label={
                    course.status === 'published'
                      ? `Unpublish ${course.title}`
                      : `Publish ${course.title}`
                  }
                  size="small"
                  onClick={() => handlePublishToggle(course)}
                >
                  {course.status === 'published' ? <UnpublishedIcon /> : <PublishIcon />}
                </IconButton>
                <IconButton
                  aria-label={`Delete ${course.title}`}
                  size="small"
                  color="error"
                  onClick={() => handleDelete(course.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          )}
        />
      )}

      {tab === 1 && (
        <DataTable
          data={enquiries}
          loading={loadingEnquiries}
          searchFields={['user_name', 'user_phone', 'user_email', 'course_title', 'user_location', 'message']}
          emptyMessage="No course enquiries yet"
          tableMinWidth={1550}
          columns={[
            { field: 'id', label: 'ID', minWidth: 70 },
            { field: 'created_at', label: 'Date & Time', minWidth: 160 },
            { field: 'user_name', label: 'Candidate Name', minWidth: 180 },
            { field: 'user_phone', label: 'Phone & WhatsApp', minWidth: 180 },
            { field: 'user_email', label: 'Email', minWidth: 200 },
            { field: 'course_title', label: 'Course Title', minWidth: 200 },
            { field: 'user_location', label: 'Location', minWidth: 140 },
            { field: 'message', label: 'Message / Query', minWidth: 240, maxWidth: 360 },
            { field: 'status', label: 'Status', minWidth: 150 },
            { field: 'actions', label: 'Actions', minWidth: 120 },
          ]}
          renderRow={(enquiry) => (
            <TableRow key={enquiry.id} hover>
              <TableCell sx={{ minWidth: 70, whiteSpace: 'nowrap', fontWeight: 600 }}>{enquiry.id}</TableCell>
              <TableCell sx={{ minWidth: 160, whiteSpace: 'nowrap' }}>
                {enquiry.created_at
                  ? new Date(enquiry.created_at).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '-'}
              </TableCell>
              <TableCell sx={{ minWidth: 180 }}>
                <Typography fontWeight={600} variant="body2">
                  {enquiry.user_name || 'Guest'}
                </Typography>
                {enquiry.user_category && (
                  <Typography variant="caption" color="text.secondary">
                    {enquiry.user_category}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ minWidth: 180, whiteSpace: 'nowrap' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                    {enquiry.user_phone || '-'}
                  </Typography>
                  {enquiry.user_phone && (
                    <Tooltip title="Chat with Candidate on WhatsApp">
                      <IconButton
                        size="small"
                        color="success"
                        sx={{ bgcolor: 'rgba(46, 125, 50, 0.08)', '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.16)' } }}
                        onClick={() =>
                          openWhatsAppChat(
                            enquiry.user_phone,
                            enquiry.user_name,
                            enquiry.course_title
                          )
                        }
                      >
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: 200, whiteSpace: 'nowrap' }}>{enquiry.user_email || '-'}</TableCell>
              <TableCell sx={{ minWidth: 200 }}>
                <Typography fontWeight={600} variant="body2" color="primary.main">
                  {enquiry.course_title}
                </Typography>
              </TableCell>
              <TableCell sx={{ minWidth: 140 }}>{enquiry.user_location || '-'}</TableCell>
              <TableCell sx={{ minWidth: 240, maxWidth: 360 }}>
                <Tooltip title={enquiry.message ? "Click to view full message" : ""}>
                  <Typography
                    variant="body2"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      fontSize: '0.825rem',
                      cursor: enquiry.message ? 'pointer' : 'default',
                      color: enquiry.message ? 'text.primary' : 'text.secondary',
                      bgcolor: enquiry.message ? 'grey.50' : 'transparent',
                      p: enquiry.message ? 0.75 : 0,
                      borderRadius: 1,
                      border: enquiry.message ? '1px solid #e0e0e0' : 'none',
                    }}
                    onClick={() => enquiry.message && setSelectedEnquiry(enquiry)}
                  >
                    {enquiry.message || '-'}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ minWidth: 150, whiteSpace: 'nowrap' }}>
                <FormControl size="small" variant="outlined" sx={{ minWidth: 130 }}>
                  <Select
                    value={enquiry.status || 'new'}
                    onChange={(e) => handleUpdateStatus(enquiry.id, e.target.value)}
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      height: 34,
                      borderRadius: 1.5,
                      color:
                        enquiry.status === 'enrolled'
                          ? 'success.main'
                          : enquiry.status === 'contacted'
                            ? 'info.main'
                            : enquiry.status === 'cancelled'
                              ? 'error.main'
                              : 'warning.main',
                    }}
                  >
                    <MenuItem value="new">New Lead</MenuItem>
                    <MenuItem value="contacted">Contacted</MenuItem>
                    <MenuItem value="enrolled">Enrolled</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell sx={{ minWidth: 120, whiteSpace: 'nowrap' }}>
                <Tooltip title="View Details">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setSelectedEnquiry(enquiry)}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Enquiry">
                  <IconButton
                    aria-label="Delete enquiry"
                    size="small"
                    color="error"
                    onClick={() => handleDeleteEnquiry(enquiry.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          )}
        />
      )}

      {/* Course Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Course' : 'Add Course'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            required
            label="Course title"
            value={formData.title}
            onChange={(event) => setFormData({ ...formData, title: event.target.value })}
            margin="normal"
            inputProps={{ maxLength: 255 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Course Type"
                value={formData.course_type}
                onChange={(event) => setFormData({ ...formData, course_type: event.target.value })}
                margin="normal"
              >
                <MenuItem value="Certification">Certification</MenuItem>
                <MenuItem value="Workshop">Workshop</MenuItem>
                <MenuItem value="Free">Free</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={formData.status}
                onChange={(event) => setFormData({ ...formData, status: event.target.value })}
                margin="normal"
              >
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Price (e.g. Rs. 14,999)"
                value={formData.price}
                onChange={(event) => setFormData({ ...formData, price: event.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Duration (e.g. 3 Months)"
                value={formData.duration}
                onChange={(event) => setFormData({ ...formData, duration: event.target.value })}
                margin="normal"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Seats Left (e.g. 10)"
                value={formData.seats_left}
                onChange={(event) => setFormData({ ...formData, seats_left: event.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="EMI (e.g. 2,499)"
                value={formData.emi}
                onChange={(event) => setFormData({ ...formData, emi: event.target.value })}
                margin="normal"
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Thumbnail URL"
            value={formData.thumbnail_url}
            onChange={(event) => setFormData({ ...formData, thumbnail_url: event.target.value })}
            margin="normal"
            inputProps={{ maxLength: 1000 }}
            helperText="Direct image URL (e.g. https://images.unsplash.com/...)"
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(event) => setFormData({ ...formData, description: event.target.value })}
            margin="normal"
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.title.trim()}>
            {editing ? 'Save Changes' : 'Create Course'}
          </Button>
        </DialogActions>
      </Dialog>
    
      {/* Enquiry Details Dialog */}
      <Dialog
        open={Boolean(selectedEnquiry)}
        onClose={() => setSelectedEnquiry(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enquiry Details</DialogTitle>
        <DialogContent dividers>
          {selectedEnquiry && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Course</Typography>
                <Typography variant="h6">{selectedEnquiry.course_title}</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Candidate Name</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedEnquiry.user_name || 'Guest'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedEnquiry.user_phone || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedEnquiry.user_email || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Location</Typography>
                  <Typography variant="body1">{selectedEnquiry.user_location || '-'}</Typography>
                </Grid>
              </Grid>
              <Box>
                <Typography variant="caption" color="text.secondary">Message / Enquiry</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 0.5, backgroundColor: 'grey.50', whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto' }}>
                  <Typography variant="body2">{selectedEnquiry.message || 'No message provided'}</Typography>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedEnquiry?.user_phone && (
            <Button
              startIcon={<WhatsAppIcon />}
              color="success"
              variant="contained"
              onClick={() => openWhatsAppChat(selectedEnquiry.user_phone, selectedEnquiry.user_name, selectedEnquiry.course_title)}
            >
              Chat on WhatsApp
            </Button>
          )}
          <Button onClick={() => setSelectedEnquiry(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Courses;
